import { createFileRoute, useParams, useRouter, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { regionApi } from "@/lib/api/admin-api";
import { useLingui } from "@lingui/react/macro";
import {
  ArrowLeft,
  MoreHorizontal,
  PlusCircle,
  SquarePen,
  MapPin,
  Power,
  Share2,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ActivityLogTable } from "@/components/activity/ActivityLogTable";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { Region } from "@/types/region";
import { RegionLocationsTab } from "@/components/regions/RegionLocationsTab";
import { RegionRecipesTab } from "@/components/regions/RegionRecipesTab";
import { AddLocationsDialog } from "@/components/regions/AddLocationsDialog";
import { CreateLocationDialog } from "@/components/locations";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";

export const Route = createFileRoute(
  "/(protected)/_layout/regions_/$regionId",
)({
  component: RegionDetailPage,
});

type Tab = "general" | "recipes" | "locations" | "access" | "audit";

function RegionDetailPage() {
  const { t } = useLingui();
  const { regionId } = useParams({
    from: "/(protected)/_layout/regions_/$regionId",
  });
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isTablet = useIsTablet();
  const [activeTab, setActiveTab] = useState<Tab>("general");
  const [removeOpen, setRemoveOpen] = useState(false);
  const [addLocationsOpen, setAddLocationsOpen] = useState(false);

  const regionIdNum = Number(regionId);
  const isValidId = Number.isFinite(regionIdNum) && regionIdNum > 0;

  const { data: region, isLoading } = useQuery({
    queryKey: ["regions", regionIdNum],
    queryFn: () => regionApi.getById(regionIdNum, ["locations"]),
    enabled: isAuthenticated && isValidId,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: () => regionApi.update(regionIdNum, { isActive: !region?.isActive }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ["regions", regionIdNum] });
      void queryClient.invalidateQueries({ queryKey: ["regions", "list"] });
      toast.success(updated.isActive ? t`Region set as active` : t`Region set as inactive`);
    },
    onError: () => toast.error(t`Failed to update region status`),
  });

  const deleteMutation = useMutation({
    mutationFn: () => regionApi.delete(regionIdNum),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["regions", regionIdNum] });
      void queryClient.invalidateQueries({ queryKey: ["regions", "list"] });
      toast.success(t`Region removed`);
      void router.navigate({ to: "/regions", search: { page: 1, pageSize: 50 } });
    },
    onError: () => toast.error(t`Failed to remove region`),
  });

  const handleShare = () => {
    void navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success(t`Link copied to clipboard`);
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5 p-5">
        <Skeleton className="h-6 w-40" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-12 w-72" />
          <Skeleton className="h-5 w-52" />
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="flex flex-col gap-5">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!region) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <MapPin className="h-12 w-12 text-(--color-text-secondary)/30" />
        <p className="text-(--color-text-secondary)">{t`Region not found`}</p>
        <Button variant="outline" onClick={() => router.history.back()}>
          {t`Back to Regions`}
        </Button>
      </div>
    );
  }

  const locationCount = region._count?.locations ?? region.locations?.length ?? 0;

  const tabs: { id: Tab; label: string }[] = [
    { id: "general", label: t`General Details` },
    { id: "recipes", label: t`Recipes` },
    { id: "locations", label: t`Locations` },
    { id: "access", label: t`Access Control` },
    { id: "audit", label: t`Audit Logs` },
  ];

  const actionH = isTablet ? "h-10" : "h-13";

  // ── Shared actions (reused by the desktop hero row and the tablet top bar) ──
  const moreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(actionH, "rounded-[10px] px-4 border-(--shell-border) gap-2")}
        >
          <MoreHorizontal className="size-5" />
          <span className="text-base font-medium">{t`More`}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem
          className="gap-2 cursor-pointer"
          disabled={toggleActiveMutation.isPending}
          onSelect={() => toggleActiveMutation.mutate()}
        >
          <Power className="size-4" />
          {region.isActive ? t`Set as inactive` : t`Set as active`}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2 cursor-pointer"
          onSelect={() => setAddLocationsOpen(true)}
        >
          <MapPin className="size-4" />
          {t`Add location`}
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2 cursor-pointer" onSelect={handleShare}>
          <Share2 className="size-4" />
          {t`Share`}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="gap-2 cursor-pointer text-(--color-danger-text) focus:text-(--color-danger-text)"
          onSelect={() => setRemoveOpen(true)}
        >
          <Trash2 className="size-4" />
          {t`Remove`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const createLocationAction = (
    <CreateLocationDialog
      defaultRegionId={region.id}
      onCreated={() => {
        void queryClient.invalidateQueries({ queryKey: ["regions", regionIdNum] });
        void queryClient.invalidateQueries({ queryKey: ["locations"] });
      }}
      trigger={(
        <Button
          variant="outline"
          className={cn(actionH, "rounded-[10px] px-4 border-(--shell-border) gap-2")}
        >
          <PlusCircle className="size-5" />
          <span className="text-base font-medium">{t`Create location`}</span>
        </Button>
      )}
    />
  );

  const editAction = (
    <Button className={cn(actionH, "rounded-[10px] px-4 bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) gap-2")}>
      <SquarePen className="size-5" />
      <span className="text-base font-medium">{t`Edit region`}</span>
    </Button>
  );

  return (
    <div className="flex flex-col gap-5 p-3">
      {/* Tablet: compact top-bar header rendered into the app header slot */}
      {isTablet && (
        <PageHeader>
          <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
            <Link
              to="/regions"
              search={{ page: 1, pageSize: 50 }}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) transition-colors hover:bg-(--color-surface-muted)"
              title={t`Back`}
            >
              <ArrowLeft className="size-5" />
            </Link>
            <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary) truncate">
              {region.name}
            </h1>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold shrink-0",
                region.isActive
                  ? "bg-(--color-success-bg) text-(--color-success-text)"
                  : "bg-(--color-surface-muted) text-(--color-text-secondary)",
              )}
            >
              {region.isActive ? t`Active` : t`Inactive`}
            </span>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {moreMenu}
              {createLocationAction}
              {editAction}
            </div>
          </div>
        </PageHeader>
      )}

      {/* Page header */}
      {!isTablet && (
        <div className="flex flex-col gap-2">
          {/* Back link */}
          <Link
            to="/regions"
            search={{ page: 1, pageSize: 50 }}
            className="flex items-center gap-2 px-3 py-2 w-fit rounded-lg hover:bg-(--color-surface-accent) transition-colors"
          >
            <ArrowLeft className="size-5 text-(--color-text-secondary)" />
            <span className="text-base font-medium text-(--color-text-secondary)">{t`Back to Regions`}</span>
          </Link>

          {/* Hero */}
          <div className="flex items-start justify-between px-3">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-4">
                <h1 className="text-[48px] font-semibold text-(--color-text-primary) leading-none">
                  {region.name}
                </h1>
                <Badge
                  className={cn(
                    "rounded-full px-3 py-1 text-sm font-medium border-transparent shrink-0",
                    region.isActive
                      ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                      : "bg-(--color-surface-chip-inactive) text-(--color-text-primary)",
                  )}
                >
                  {region.isActive ? t`Active` : t`Inactive`}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-base">
                <span className="text-(--color-text-secondary)">{t`Locations:`}</span>
                <span className="font-medium text-(--color-text-primary)">{locationCount}</span>
                <span className="text-(--color-text-secondary) font-medium">•</span>
                <span className="text-(--color-text-secondary)">{t`Region code:`}</span>
                <span className="font-medium text-(--color-text-primary)">{region.code}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {moreMenu}
              {createLocationAction}
              {editAction}
            </div>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-(--shell-border)" />

      {/* Tab bar */}
      <div className="flex gap-1 bg-(--color-surface-muted) rounded-md p-1 w-full">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap",
              activeTab === tab.id
                ? "bg-(--color-surface-content) font-medium text-(--color-text-primary) shadow-xs"
                : "font-normal text-(--color-text-secondary) hover:text-(--color-text-secondary)",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "general" && <GeneralDetailsTab region={region} />}
      {activeTab === "recipes" && <RegionRecipesTab regionId={regionIdNum} />}
      {activeTab === "locations" && <RegionLocationsTab regionId={regionIdNum} />}
      {activeTab === "audit" && (
        <ActivityLogTable scope={{ entityType: "region", entityId: regionIdNum }} showActor />
      )}
      {activeTab !== "general" && activeTab !== "recipes" && activeTab !== "locations" && activeTab !== "audit" && (
        <div className="py-16 text-center text-sm text-(--color-text-secondary)">
          {t`Coming soon`}
        </div>
      )}

      <AddLocationsDialog
        open={addLocationsOpen}
        onOpenChange={setAddLocationsOpen}
        regionId={regionIdNum}
        regionName={region.name}
      />

      <ConfirmDeleteDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        title={t`Remove region?`}
        description={t`This will permanently remove ${region.name} from your organization.`}
        bullets={[t`This action cannot be undone`]}
        confirmLabel={t`Remove region`}
        onConfirm={() => deleteMutation.mutate()}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}

function FormRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="flex gap-8 items-start">
        <span className="w-85 shrink-0 text-sm font-medium text-(--color-text-primary) leading-5">
          {label}
        </span>
        <div className="flex-1">{children}</div>
      </div>
      <div className="h-px bg-(--shell-border)" />
    </>
  );
}

function GeneralDetailsTab({ region }: { region: Region }) {
  const { t } = useLingui();

  return (
    <div className="px-4">
      {/* Section header */}
      <div className="flex flex-col gap-5 pb-px">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-(--color-text-primary) leading-7">
              {t`General Details`}
            </h2>
            <p className="text-sm text-(--color-text-secondary) leading-5">
              {t`View and manage region information.`}
            </p>
          </div>
        </div>
        <div className="h-px bg-(--shell-border)" />
      </div>

      {/* Form rows */}
      <div className="flex flex-col gap-5 mt-5">
        <FormRow label={t`Name`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {region.name}
          </span>
        </FormRow>

        <FormRow label={t`Code`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {region.code}
          </span>
        </FormRow>

        <FormRow label={t`Status`}>
          <Badge
            className={cn(
              "rounded-full px-3 py-1 text-sm font-medium border-transparent",
              region.isActive
                ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                : "bg-(--color-surface-chip-inactive) text-(--color-text-primary)",
            )}
          >
            {region.isActive ? t`Active` : t`Inactive`}
          </Badge>
        </FormRow>

        {region.timezone && (
          <FormRow label={t`Timezone`}>
            <span className="text-base font-medium text-(--color-text-primary)">
              {region.timezone}
            </span>
          </FormRow>
        )}

        {region.organization && (
          <FormRow label={t`Organization`}>
            <span className="text-base font-medium text-(--color-text-primary)">
              {region.organization.name}
            </span>
          </FormRow>
        )}
      </div>
    </div>
  );
}
